import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSignedThumbs } from "@/lib/photoUrl";
import mapaImg from "@/assets/mapa-nahled.jpg";
import { pickDescription, tKind, useT } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { MapPin } from "lucide-react";
import type { BusinessKind } from "@/lib/admin-labels";
import { AutoFitText } from "@/components/AutoFitText";
import { RotatingCover } from "@/components/RotatingCover";



import { HOME_META, pickMeta } from "@/lib/page-meta";

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>): { lang?: string; pendingVerification?: true } => ({
    lang: typeof search.lang === "string" ? search.lang : undefined,
    pendingVerification: search.pendingVerification === true ? true : undefined,
  }),
  head: ({ match }) => {
    const m = pickMeta(HOME_META, (match.search as { lang?: string }).lang);
    const url = "https://cerveza.international/";
    return {
      meta: [
        { title: m.title },
        { name: "description", content: m.description },
        { property: "og:title", content: m.title },
        { property: "og:description", content: m.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Index,
});

function Index() {
  const search = Route.useSearch();
  const showVerify = (search as any)?.pendingVerification === true;
  const { t, lang } = useT();

  const { data: latest = [] } = useQuery({
    queryKey: ["latest-businesses-home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("id, name, kind, city, country, description, descriptions, submitted_at")
        .eq("status", "approved")
        .order("submitted_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        name: string;
        kind: BusinessKind;
        city: string;
        country: string;
        description: string | null;
        descriptions: Record<string, string> | null;
        submitted_at: string;
      }>;
    },
  });

  const ids = useMemo(() => latest.map((b) => b.id), [latest]);
  const idsKey = useMemo(() => ids.join(","), [ids]);
  const { data: covers } = useQuery({
    enabled: ids.length > 0,
    queryKey: ["latest-businesses-covers", idsKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_photos")
        .select("business_id, storage_path, created_at")
        .in("business_id", ids)
        .eq("status", "active")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [coverUrls, setCoverUrls] = useState<Record<string, string[]>>({});
  useEffect(() => {
    if (!covers) return;
    let cancelled = false;

    const applyIfChanged = (next: Record<string, string[]>) => {
      if (cancelled) return;
      setCoverUrls((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) return next;
        for (const k of nextKeys) {
          const a = prev[k] ?? [];
          const b = next[k] ?? [];
          if (a.length !== b.length) return next;
          for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return next;
        }
        return prev;
      });
    };

    (async () => {
      if (covers.length === 0) {
        applyIfChanged({});
        return;
      }
      const grouped: Record<string, string[]> = {};
      for (const c of covers) {
        (grouped[c.business_id] ??= []).push(c.storage_path);
      }
      const items: Array<{ id: string; storage_path: string }> = [];
      const keyToBiz: Record<string, { bizId: string; index: number }> = {};
      for (const [bizId, paths] of Object.entries(grouped)) {
        paths.slice(0, 6).forEach((sp, index) => {
          const key = `${bizId}::${index}`;
          items.push({ id: key, storage_path: sp });
          keyToBiz[key] = { bizId, index };
        });
      }
      const signed = await getSignedThumbs(items, { width: 600, height: 450, quality: 70 });
      const out: Record<string, string[]> = {};
      for (const [key, url] of Object.entries(signed)) {
        const meta = keyToBiz[key];
        if (!meta) continue;
        (out[meta.bizId] ??= [])[meta.index] = url;
      }
      for (const k of Object.keys(out)) out[k] = out[k].filter(Boolean);
      applyIfChanged(out);
    })();

    return () => { cancelled = true; };
  }, [covers]);



  const kategorie: Array<{ ikona: string; nazev: string; kind: BusinessKind }> = [
    { ikona: "🍽️", nazev: t("cat.restaurace"),       kind: "restaurace" },
    { ikona: "🍸", nazev: t("cat.bary"),             kind: "bar" },
    { ikona: "☕", nazev: t("cat.kavarny"),          kind: "kavarna" },
    { ikona: "🍵", nazev: t("cat.cajovny"),          kind: "cajovna" },
    { ikona: "🌮", nazev: t("cat.stanky"),           kind: "stanek" },
    { ikona: "🥗", nazev: t("cat.jidelny"),          kind: "jidelna" },
    { ikona: "🥛", nazev: t("cat.mlecnyBar"),        kind: "mlecny_bar" },
    { ikona: "🚚", nazev: t("cat.foodTruck"),        kind: "food_truck" },
    { ikona: "🍔", nazev: t("cat.rychleObcerstveni"), kind: "rychle_obcerstveni" },
    { ikona: "🥪", nazev: t("cat.bistra"),           kind: "bistro" },
  ];


  return (
    <div className="min-h-screen bg-brand-cream text-foreground">
      {showVerify && (
        <div className="mx-auto max-w-4xl px-4 pt-6">
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 shadow-sm">
            {t("auth.checkEmail")}
          </div>
        </div>
      )}
      {/* Hero */}
      <header className="relative overflow-hidden px-4 py-14 sm:px-6 sm:py-20 md:py-24">
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <h1 className="mb-4 font-serif text-4xl font-extrabold leading-tight text-brand-earth sm:mb-6 sm:text-5xl md:text-7xl">
            {t("home.title.a")}{" "}
            <span className="italic underline decoration-brand-terracotta/30">{t("home.title.b")}</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-base text-muted-foreground sm:mb-12 sm:text-lg">
            {t("home.subtitle")}
          </p>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="mx-auto flex max-w-3xl flex-col gap-2 rounded-2xl border border-brand-earth/5 bg-card p-2 shadow-xl shadow-brand-earth/5 md:flex-row"
          >
            <input
              type="text"
              placeholder={t("home.search.what")}
              className="flex-1 rounded-xl border-transparent bg-brand-cream/40 px-6 py-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-terracotta/30"
            />
            <input
              type="text"
              placeholder={t("home.search.where")}
              className="flex-1 rounded-xl border-transparent bg-brand-cream/40 px-6 py-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-terracotta/30"
            />
            <button
              type="submit"
              className="rounded-xl bg-brand-terracotta px-8 py-4 font-bold text-brand-cream transition-all hover:brightness-110"
            >
              {t("home.search.submit")}
            </button>
          </form>
        </div>
      </header>

      {/* Kategorie */}
      <section id="katalog" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-5 lg:grid-cols-10">
          {kategorie.map((k) => (
            <Link
              key={k.nazev}
              to="/mapa"
              search={{ kind: k.kind }}
              className="group cursor-pointer rounded-2xl border border-brand-earth/5 bg-card p-3 text-center transition-all hover:border-brand-terracotta/40 sm:p-4"
            >
              <div className="mb-2 text-2xl transition-transform group-hover:scale-110 sm:mb-3 sm:text-3xl">
                {k.ikona}
              </div>
              <AutoFitText
                className="font-bold leading-tight text-foreground"
                maxFontSize={12}
                minFontSize={8}
              >
                {k.nazev}
              </AutoFitText>
            </Link>
          ))}
        </div>
      </section>

      {/* Mapa */}
      <section id="mapa" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="relative h-56 overflow-hidden rounded-2xl border-4 border-card bg-muted shadow-2xl sm:h-72 md:h-80 md:rounded-3xl">
          <img
            src={mapaImg}
            alt={t("home.mapaCta")}
            width={1600}
            height={600}
            loading="lazy"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-brand-earth/10">
            <a href="/mapa" className="rounded-full bg-brand-earth px-6 py-3 text-sm font-bold text-brand-cream shadow-xl transition-all hover:bg-brand-terracotta sm:px-8 sm:text-base">
              {t("home.mapaCta")}
            </a>
          </div>
        </div>
      </section>

      {/* Nejnovější podniky */}
      <section id="nejnovejsi" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-bold text-brand-earth">
              {t("home.latest.title")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("home.latest.sub")}</p>
          </div>
          <Link
            to="/mapa"
            className="text-sm font-semibold text-brand-terracotta hover:underline"
          >
            {t("home.latest.viewAll")}
          </Link>
        </div>

        {latest.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("home.latest.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {latest.map((b) => {
              const desc = pickDescription(lang, b.descriptions, b.description);
              return (
                <Link
                  key={b.id}
                  to="/podnik/$id"
                  params={{ id: b.id }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-brand-earth/10 bg-card transition-all hover:-translate-y-0.5 hover:border-brand-terracotta/40 hover:shadow-lg"
                >
                  <div className="aspect-square w-full overflow-hidden bg-brand-cream/60 sm:aspect-[4/3] lg:aspect-video">
                    {coverUrls[b.id]?.length ? (
                      <RotatingCover urls={coverUrls[b.id]} alt={b.name} />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-3xl text-brand-earth/30">
                        🍽️
                      </div>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {tKind(t, b.kind)}
                    </Badge>
                  </div>
                  <h3 className="mb-1 line-clamp-2 font-serif text-lg font-bold text-brand-earth group-hover:text-brand-terracotta">
                    {b.name}
                  </h3>
                  <p className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {b.city}, {b.country}
                  </p>
                  {desc && (
                    <p className="line-clamp-3 text-sm text-foreground/80">{desc}</p>
                  )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>




      {/* CTA pro majitele */}
      <section id="majitele" className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-24">
        <h2 className="mb-4 font-serif text-2xl font-bold text-brand-earth sm:text-3xl">
          {t("owner.title")}
        </h2>
        <p className="mb-8 text-sm text-muted-foreground sm:text-base">
          {t("owner.text")}
        </p>
        <a href="/registrace-podniku" className="inline-block rounded-full bg-brand-terracotta px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-brand-cream shadow-lg transition-all hover:-translate-y-1 hover:shadow-brand-terracotta/30 sm:px-10 sm:py-4">
          {t("owner.cta")}
        </a>
      </section>

      {/* Footer */}
      <footer className="border-t border-brand-earth/10 px-4 py-10 sm:px-6 sm:py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
          <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-2">
            <span className="font-serif text-xl font-bold text-brand-earth">
              Cerveza<span className="text-brand-terracotta">.</span>International
            </span>
            <span className="text-xs text-muted-foreground sm:text-sm">
              © {new Date().getFullYear()} {t("footer.rights")}
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <a href="#" className="hover:text-brand-terracotta">{t("footer.about")}</a>
            <a href="#" className="hover:text-brand-terracotta">{t("footer.terms")}</a>
            <a href="#" className="hover:text-brand-terracotta">{t("footer.privacy")}</a>
            <a href="#" className="hover:text-brand-terracotta">{t("footer.contact")}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
